import React, { useState, useEffect, useRef } from 'react';
import { Card, Table, Button, Tag, Space, Select, DatePicker, message, Progress, Spin, Statistic, Row, Col } from 'antd';
import { PlayCircleOutlined, ReloadOutlined, CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined, DatabaseOutlined } from '@ant-design/icons';
import moment from 'moment';
import { formatIndianNumber } from '../../../utils/numberFormat';
import { getApiEndpoint, authFetch } from '../../../config/apiBase';

const { Option } = Select;

const SmsSummaryFetchPage = () => {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(0); // 0 = all users
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [progress, setProgress] = useState(null);
  const [logs, setLogs] = useState([]);
  const [summaryData, setSummaryData] = useState([]);
  const [summaryTotal, setSummaryTotal] = useState(0);
  const [summaryPage, setSummaryPage] = useState(1);
  const [summaryPageSize, setSummaryPageSize] = useState(50);
  const [dataLoading, setDataLoading] = useState(false);
  const [aggregates, setAggregates] = useState(null);
  const [activeTab, setActiveTab] = useState('fetch'); // fetch or data
  const eventSourceRef = useRef(null);
  const logsEndRef = useRef(null);

  // Fetch users on mount
  useEffect(() => {
    fetchUsers();
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, []);

  // Auto-scroll logs
  useEffect(() => {
    if (logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs]);

  const fetchUsers = async () => {
    try {
      const response = await authFetch(getApiEndpoint('/migration/summary-fetch/users'));
      if (response.ok) {
        const result = await response.json();
        setUsers(result.data || []);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchSummaryData = async (page = 1, pageSize = 50) => {
    setDataLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedUser > 0) params.append('user_id', selectedUser);
      if (fromDate) params.append('from_date', fromDate.format('DD-MM-YYYY'));
      if (toDate) params.append('to_date', toDate.format('DD-MM-YYYY'));
      params.append('page', page);
      params.append('limit', pageSize);

      const response = await authFetch(getApiEndpoint(`/migration/summary-fetch/data?${params}`));
      if (response.ok) {
        const result = await response.json();
        setSummaryData(result.data || []);
        setSummaryTotal(result.total || 0);
        setSummaryPage(page);
        setSummaryPageSize(pageSize);
        setAggregates(result.aggregates || null);
      }
    } catch (error) {
      message.error('Failed to fetch summary data');
    } finally {
      setDataLoading(false);
    }
  };

  const startFetch = () => {
    if (!fromDate || !toDate) {
      message.warning('Please select both From Date and To Date');
      return;
    }

    setFetching(true);
    setLogs([]);
    setProgress({
      insertedCount: 0,
      errorCount: 0,
      totalCount: 0,
      totalUsers: 0,
      totalDates: 0,
      currentUser: 0,
      currentDate: 0,
    });

    const params = new URLSearchParams();
    params.append('from_date', fromDate.format('DD-MM-YYYY'));
    params.append('to_date', toDate.format('DD-MM-YYYY'));
    if (selectedUser > 0) params.append('user_id', selectedUser);

    const token = localStorage.getItem('token');
    const url = getApiEndpoint(`/migration/summary-fetch/start-sse?${params}`);

    // Use EventSource with auth header via fetch
    const fetchSSE = async () => {
      try {
        const response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          const errText = await response.text();
          message.error('Failed to start fetch: ' + errText);
          setFetching(false);
          return;
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                
                setProgress(prev => ({
                  ...prev,
                  insertedCount: data.inserted_count || prev?.insertedCount || 0,
                  errorCount: data.error_count || prev?.errorCount || 0,
                  totalCount: data.total_count || prev?.totalCount || 0,
                  totalUsers: data.total_users || prev?.totalUsers || 0,
                  totalDates: data.total_dates || prev?.totalDates || 0,
                  currentUser: data.current_user || prev?.currentUser || 0,
                  currentDate: data.current_date || prev?.currentDate || 0,
                }));

                if (data.status !== 'processing') {
                  setLogs(prev => [...prev, {
                    time: new Date().toLocaleTimeString(),
                    user: data.username || '',
                    userId: data.user_id || 0,
                    date: data.date || '',
                    status: data.status,
                    message: data.message || '',
                    totalSMS: data.total_sms || 0,
                  }]);
                }

                if (data.status === 'completed') {
                  setFetching(false);
                  message.success(`Completed! Inserted: ${data.inserted_count}, Errors: ${data.error_count}`);
                  fetchUsers(); // Refresh user list to update summary_migration status
                }
              } catch (e) {
                // ignore parse errors
              }
            }
          }
        }

        setFetching(false);
      } catch (error) {
        console.error('SSE error:', error);
        message.error('Connection error: ' + error.message);
        setFetching(false);
      }
    };

    fetchSSE();
  };

  const totalProgress = progress && progress.totalCount > 0
    ? Math.round(((progress.insertedCount + progress.errorCount) / progress.totalCount) * 100)
    : 0;

  // Data table columns
  const dataColumns = [
    { title: 'User', dataIndex: 'username', key: 'username', render: (v) => v || '-' },
    { title: 'Date', dataIndex: 'date', key: 'date', render: (d) => moment(d).format('DD-MM-YYYY') },
    { title: 'Total SMS', dataIndex: 'totalSMS', key: 'totalSMS', render: (v) => formatIndianNumber(v), align: 'right' },
    { title: 'Sent', dataIndex: 'sent', key: 'sent', render: (v) => formatIndianNumber(v), align: 'right' },
    { title: 'Delivered', dataIndex: 'delivered', key: 'delivered', render: (v) => <span style={{color: '#52c41a'}}>{formatIndianNumber(v)}</span>, align: 'right' },
    { title: 'Failed', dataIndex: 'failed', key: 'failed', render: (v) => <span style={{color: v > 0 ? '#ff4d4f' : 'inherit'}}>{formatIndianNumber(v)}</span>, align: 'right' },
    { title: 'Pending', dataIndex: 'pending', key: 'pending', render: (v) => formatIndianNumber(v), align: 'right' },
    { title: 'In Queue', dataIndex: 'inQue', key: 'inQue', render: (v) => formatIndianNumber(v), align: 'right' },
    { title: 'Undelivered', dataIndex: 'undelivered', key: 'undelivered', render: (v) => formatIndianNumber(v), align: 'right' },
    { title: 'Blocked', dataIndex: 'block', key: 'block', render: (v) => formatIndianNumber(v), align: 'right' },
    { title: 'NDNC', dataIndex: 'NDNCDenied', key: 'NDNCDenied', render: (v) => formatIndianNumber(v), align: 'right' },
    { title: 'DR N/A', dataIndex: 'drNotAvailable', key: 'drNotAvailable', render: (v) => formatIndianNumber(v), align: 'right' },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card 
        title={
          <span>
            <DatabaseOutlined style={{ marginRight: 8 }} />
            SMS Summary Fetch (API)
          </span>
        }
        extra={
          <Space>
            <Button 
              type={activeTab === 'fetch' ? 'primary' : 'default'}
              onClick={() => setActiveTab('fetch')}
            >
              Fetch from API
            </Button>
            <Button 
              type={activeTab === 'data' ? 'primary' : 'default'}
              onClick={() => { setActiveTab('data'); fetchSummaryData(); }}
            >
              View Data
            </Button>
          </Space>
        }
      >
        {activeTab === 'fetch' && (
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            {/* Controls */}
            <Card size="small" title="Fetch Configuration">
              <Row gutter={16} align="middle">
                <Col>
                  <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, fontSize: 12 }}>From Date</label>
                  <DatePicker
                    value={fromDate}
                    onChange={setFromDate}
                    format="DD-MM-YYYY"
                    placeholder="From Date"
                    disabled={fetching}
                  />
                </Col>
                <Col>
                  <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, fontSize: 12 }}>To Date</label>
                  <DatePicker
                    value={toDate}
                    onChange={setToDate}
                    format="DD-MM-YYYY"
                    placeholder="To Date"
                    disabled={fetching}
                  />
                </Col>
                <Col>
                  <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, fontSize: 12 }}>User</label>
                  <Select
                    style={{ width: 280 }}
                    placeholder="All Users"
                    value={selectedUser}
                    onChange={setSelectedUser}
                    disabled={fetching}
                    showSearch
                    optionFilterProp="label"
                  >
                    <Option value={0} label="All Users">All Users (with API Key)</Option>
                    {users.map(user => (
                      <Option key={user.id} value={user.id} label={user.username}>
                        {user.username} {user.summary_migration ? '✅' : ''}
                      </Option>
                    ))}
                  </Select>
                </Col>
                <Col>
                  <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, fontSize: 12 }}>&nbsp;</label>
                  <Button
                    type="primary"
                    icon={fetching ? <LoadingOutlined /> : <PlayCircleOutlined />}
                    onClick={startFetch}
                    loading={fetching}
                    disabled={!fromDate || !toDate}
                    size="large"
                  >
                    {fetching ? 'Fetching...' : 'Start Fetch'}
                  </Button>
                </Col>
              </Row>

              {/* User stats */}
              <div style={{ marginTop: 12 }}>
                <Tag color="blue">{users.length} total users</Tag>
                <Tag color="green">{users.filter(u => u.has_api_key).length} with API key</Tag>
                <Tag color="orange">{users.filter(u => u.summary_migration).length} already migrated</Tag>
              </div>
            </Card>

            {/* Progress */}
            {progress && (progress.totalCount > 0 || fetching) && (
              <Card size="small" title="Progress">
                <Row gutter={16}>
                  <Col span={6}>
                    <Statistic
                      title="Total Requests"
                      value={progress.insertedCount + progress.errorCount}
                      suffix={`/ ${progress.totalCount || '...'}`}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic
                      title="Inserted"
                      value={progress.insertedCount}
                      valueStyle={{ color: '#3f8600' }}
                      prefix={<CheckCircleOutlined />}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic
                      title="Errors"
                      value={progress.errorCount}
                      valueStyle={{ color: progress.errorCount > 0 ? '#cf1322' : 'inherit' }}
                      prefix={<CloseCircleOutlined />}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic
                      title="Users / Dates"
                      value={`${progress.currentUser || 0}/${progress.totalUsers || 0}`}
                      suffix={`• Day ${progress.currentDate || 0}/${progress.totalDates || 0}`}
                    />
                  </Col>
                </Row>
                <Progress
                  percent={totalProgress}
                  status={fetching ? 'active' : (progress.errorCount > 0 ? 'exception' : 'success')}
                  style={{ marginTop: 16 }}
                  strokeWidth={12}
                />
              </Card>
            )}

            {/* Logs */}
            {logs.length > 0 && (
              <Card 
                size="small" 
                title={`Activity Log (${logs.length} entries)`}
                bodyStyle={{ maxHeight: 400, overflow: 'auto', padding: '8px 16px' }}
              >
                <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #f0f0f0', position: 'sticky', top: 0, background: '#fff' }}>
                      <th style={{ padding: '6px 8px', textAlign: 'left' }}>Time</th>
                      <th style={{ padding: '6px 8px', textAlign: 'left' }}>User</th>
                      <th style={{ padding: '6px 8px', textAlign: 'left' }}>Date</th>
                      <th style={{ padding: '6px 8px', textAlign: 'center' }}>Status</th>
                      <th style={{ padding: '6px 8px', textAlign: 'right' }}>Total SMS</th>
                      <th style={{ padding: '6px 8px', textAlign: 'left' }}>Message</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid #fafafa' }}>
                        <td style={{ padding: '4px 8px', color: '#999' }}>{log.time}</td>
                        <td style={{ padding: '4px 8px', fontWeight: 500 }}>{log.user}</td>
                        <td style={{ padding: '4px 8px' }}>{log.date}</td>
                        <td style={{ padding: '4px 8px', textAlign: 'center' }}>
                          {log.status === 'success' ? (
                            <Tag color="success" style={{ margin: 0 }}>OK</Tag>
                          ) : log.status === 'error' ? (
                            <Tag color="error" style={{ margin: 0 }}>ERR</Tag>
                          ) : log.status === 'completed' ? (
                            <Tag color="blue" style={{ margin: 0 }}>DONE</Tag>
                          ) : (
                            <Tag style={{ margin: 0 }}>{log.status}</Tag>
                          )}
                        </td>
                        <td style={{ padding: '4px 8px', textAlign: 'right' }}>
                          {log.totalSMS > 0 ? formatIndianNumber(log.totalSMS) : '-'}
                        </td>
                        <td style={{ padding: '4px 8px', color: log.status === 'error' ? '#ff4d4f' : '#666', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {log.message}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div ref={logsEndRef} />
              </Card>
            )}
          </Space>
        )}

        {activeTab === 'data' && (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Space>
              <Select
                style={{ width: 200 }}
                placeholder="All Users"
                value={selectedUser}
                onChange={setSelectedUser}
                showSearch
                allowClear
                optionFilterProp="label"
              >
                <Option value={0}>All Users</Option>
                {users.map(user => (
                  <Option key={user.id} value={user.id}>
                    {user.username}
                  </Option>
                ))}
              </Select>
              <DatePicker
                value={fromDate}
                onChange={setFromDate}
                format="DD-MM-YYYY"
                placeholder="From Date"
              />
              <DatePicker
                value={toDate}
                onChange={setToDate}
                format="DD-MM-YYYY"
                placeholder="To Date"
              />
              <Button type="primary" onClick={() => fetchSummaryData(1, summaryPageSize)} loading={dataLoading}>
                Search
              </Button>
              <Button icon={<ReloadOutlined />} onClick={() => fetchSummaryData(summaryPage, summaryPageSize)}>
                Refresh
              </Button>
            </Space>

            <Table
              columns={dataColumns}
              dataSource={summaryData}
              rowKey="id"
              loading={dataLoading}
              size="small"
              pagination={{
                current: summaryPage,
                pageSize: summaryPageSize,
                total: summaryTotal,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${formatIndianNumber(total)} records`,
                onChange: (page, size) => fetchSummaryData(page, size),
              }}
              summary={() => {
                if (!aggregates) return null;
                return (
                  <Table.Summary fixed>
                    <Table.Summary.Row style={{ background: '#fafafa' }}>
                      <Table.Summary.Cell index={0}><strong>Overall Total ({formatIndianNumber(summaryTotal)} records)</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={1} />
                      <Table.Summary.Cell index={2} align="right"><strong>{formatIndianNumber(aggregates.totalSMS)}</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={3} align="right"><strong>{formatIndianNumber(aggregates.sent)}</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={4} align="right"><strong style={{color: '#52c41a'}}>{formatIndianNumber(aggregates.delivered)}</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={5} align="right"><strong style={{color: aggregates.failed > 0 ? '#ff4d4f' : 'inherit'}}>{formatIndianNumber(aggregates.failed)}</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={6} align="right"><strong>{formatIndianNumber(aggregates.pending)}</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={7} align="right"><strong>{formatIndianNumber(aggregates.inQue)}</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={8} align="right"><strong>{formatIndianNumber(aggregates.undelivered)}</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={9} align="right"><strong>{formatIndianNumber(aggregates.block)}</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={10} align="right"><strong>{formatIndianNumber(aggregates.NDNCDenied)}</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={11} align="right"><strong>{formatIndianNumber(aggregates.drNotAvailable)}</strong></Table.Summary.Cell>
                    </Table.Summary.Row>
                  </Table.Summary>
                );
              }}
            />
          </Space>
        )}
      </Card>
    </div>
  );
};

export default SmsSummaryFetchPage;
